import { NotificationType } from '../../../../__generated__/graphql';
import { SiteProductKind } from '../../../constants/siteConfig';
import { bookingDetailPath, claimDetailPath, claimListPath } from '../../../utils/productRoutes';

/** The notification fields that decide where a tap goes. */
export interface NotificationLink {
  type: NotificationType;
  bookingId?: string | null;
  chatId?: string | null;
  broadcastId?: string | null;
  damageClaimId?: string | null;
  navigationData?: string | null;
}

export interface NotificationTarget {
  path: string;
  state?: Record<string, unknown>;
}

export const normalizeNavigationData = (
  navigationData: string | null | undefined,
  kind: SiteProductKind,
): string | null => {
  if (!navigationData) {
    return null;
  }

  if (navigationData.startsWith('/host/damage-claims/')) {
    return navigationData.replace('/host/damage-claims/', `${claimListPath('host', kind)}/`);
  }

  if (navigationData.startsWith('/')) {
    return navigationData;
  }

  return null;
};

const isDamageClaimNotification = (type: NotificationType): boolean =>
  type === NotificationType.DamageClaimCreated ||
  type === NotificationType.DamageClaimApproved ||
  type === NotificationType.DamageClaimRejected ||
  type === NotificationType.DamageClaimResolved ||
  type === NotificationType.DamageClaimResponseSubmitted;

/**
 * Where tapping a notification goes, or null when it opens nothing.
 *
 * An `/admin/` link wins over the trip: a brand admin's notice (spec 013 P7
 * `CARDHOLDER_NAME_MISMATCH` opens `/admin/card-name-checks?bookingId=…`)
 * also carries the trip id, and an admin cannot open the host's trip page.
 */
export function notificationTarget(
  notification: NotificationLink,
  role: 'guest' | 'host' | undefined,
  kind: SiteProductKind,
): NotificationTarget | null {
  const side = role === 'host' ? 'host' : 'guest';
  const navigationData = normalizeNavigationData(notification.navigationData, kind);

  if (navigationData?.startsWith('/admin/')) {
    return { path: navigationData };
  }
  if (notification.type === NotificationType.ChatMessage && notification.chatId) {
    return { path: `/chat/${notification.chatId}`, state: { bookingId: notification.bookingId } };
  }
  if (notification.type === NotificationType.Broadcast && notification.broadcastId) {
    return { path: `/broadcast/${notification.broadcastId}` };
  }
  if (isDamageClaimNotification(notification.type)) {
    if (navigationData) return { path: navigationData };
    if (notification.damageClaimId) {
      return { path: claimDetailPath(side, notification.damageClaimId, kind) };
    }
    if (notification.bookingId) {
      return { path: bookingDetailPath(side, notification.bookingId, kind) };
    }
    return null;
  }
  if (notification.bookingId) {
    return { path: bookingDetailPath(side, notification.bookingId, kind) };
  }
  // A notice about no single trip (spec 014c delivery notices go to `/cars/host`).
  return navigationData ? { path: navigationData } : null;
}
